import React, { useRef, useState, useEffect } from 'react';
import { Button } from '@@/ui/primitives/button';
import { Badge } from '@@/ui/primitives/badge';
import { cn } from '@@/lib/utils';
import { Upload, Paperclip, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Spinner } from '@@/ui/components/Spinner';
import { useUploadCaseAttachments } from '@@/products/cases/hooks';

type FileTypeCategory = 'image' | 'pdf' | 'csv' | 'xlsx' | 'txt' | 'doc' | 'other';

// Mode 1: Upload mode (when caseId is provided)
interface UploadModeProps {
  caseId: string;
  onUploadSuccess?: () => void;
  onUploadError?: (error: Error) => void;
  files?: never;
  onFilesChange?: never;
}

// Mode 2: Local mode (when files and onFilesChange are provided)
interface LocalModeProps {
  caseId?: never;
  onUploadSuccess?: never;
  onUploadError?: never;
  files: File[];
  onFilesChange: (files: File[]) => void;
}

// Common props
interface CommonProps {
  showHeader?: boolean;
  title?: string;
  description?: string;
  variant?: 'default' | 'compact';
}

type AttachmentUploaderProps = CommonProps & (UploadModeProps | LocalModeProps);

const SUPPORTED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/bmp',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/zip'
];

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

// Get file type from MIME type
export const getFileTypeFromMime = (mimeType: string): FileTypeCategory => {
  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (mimeType === 'text/csv' || mimeType.includes('spreadsheet')) return mimeType.includes('excel') ? 'xlsx' : 'csv';
  if (mimeType === 'text/plain') return 'txt';
  if (mimeType.includes('word')) return 'doc';
  return 'other';
};

export const AttachmentUploader: React.FC<AttachmentUploaderProps> = (props) => {
  const {
    showHeader = true,
    title = 'Upload Attachments',
    description = 'Upload images, documents, and files (max 10MB per file). Supported: JPG, PNG, PDF, DOC, XLS, CSV',
    variant = 'default'
  } = props;

  const isUploadMode = 'caseId' in props && !!props.caseId;
  const caseId = isUploadMode ? props.caseId : undefined;
  const onUploadSuccess = isUploadMode ? props.onUploadSuccess : undefined;
  const onUploadError = isUploadMode ? props.onUploadError : undefined;
  const externalFiles = !isUploadMode ? props.files : undefined;
  const onFilesChange = !isUploadMode ? props.onFilesChange : undefined;

  // Internal state for upload mode
  const [internalFiles, setInternalFiles] = useState<File[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const filesToUpload = isUploadMode ? internalFiles : externalFiles || [];

  useEffect(() => {
    if (!isUploadMode && externalFiles) {
      setInternalFiles(externalFiles);
    }
  }, [isUploadMode, externalFiles]);

  const uploadMutation = useUploadCaseAttachments({
    caseId: caseId || '',
    onSuccess: () => {
      setInternalFiles([]);
      onUploadSuccess?.();
    },
    onError: onUploadError
  });

  const updateFiles = (newFiles: File[]) => {
    if (isUploadMode) {
      setInternalFiles(newFiles);
    } else {
      onFilesChange?.(newFiles);
    }
  };

  const getFileIcon = (fileType: FileTypeCategory) => {
    const iconClasses = 'h-6 w-6';
    switch (fileType) {
      case 'pdf':
        return <span className={`${iconClasses} text-red-500`}>📄</span>;
      case 'csv':
      case 'xlsx':
        return <span className={`${iconClasses} text-green-600`}>📊</span>;
      case 'txt':
      case 'doc':
        return <span className={`${iconClasses} text-muted-foreground`}>📝</span>;
      default:
        return <span className={iconClasses}>📎</span>;
    }
  };

  const validateFile = (file: File): { valid: boolean; error?: string } => {
    if (file.size > MAX_FILE_SIZE) {
      return { valid: false, error: `File size exceeds 10MB limit` };
    }
    if (!SUPPORTED_FILE_TYPES.includes(file.type)) {
      return { valid: false, error: `File type not supported` };
    }
    return { valid: true };
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files) return;

    const validFiles: File[] = [];
    const errors: string[] = [];

    Array.from(files).forEach((file) => {
      const validation = validateFile(file);
      if (validation.valid) {
        validFiles.push(file);
      } else {
        errors.push(`${file.name}: ${validation.error}`);
      }
    });

    if (validFiles.length > 0) {
      updateFiles([...filesToUpload, ...validFiles]);
    }

    if (errors.length > 0) {
      errors.forEach((error) => {
        toast.error('File not added', {
          description: error,
          duration: 5000
        });
      });
    }

    // Reset file input to allow re-selecting the same file
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const removeFile = (index: number) => {
    updateFiles(filesToUpload.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    updateFiles([]);
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const handleUploadFiles = () => {
    if (filesToUpload.length === 0) {
      toast.error('No files to upload');
      return;
    }

    if (!caseId) {
      toast.error('Case ID is required');
      return;
    }

    uploadMutation.mutate(filesToUpload);
  };

  // Compact variant for local mode
  if (variant === 'compact') {
    return (
      <div className='space-y-3'>
        {/* Upload Area */}
        <div
          className={cn(
            'border-2 border-gray-400 border-dashed rounded-lg p-6 transition-all duration-200 cursor-pointer hover:border-[var(--primary-color)]/80',
            isDragging ? 'border-cases-primary bg-emerald-50/50' : ''
          )}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type='file'
            multiple
            className='hidden'
            accept={SUPPORTED_FILE_TYPES.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
          />
          <div className='flex flex-col items-center justify-center text-center'>
            <Upload className='h-10 w-10 text-muted-foreground mb-3' />
            <p className='text-sm font-medium text-muted-foreground mb-1'>Click to upload or drag and drop</p>
            <p className='text-xs text-muted-foreground'>PDF, Images, Documents, CSV, Excel (Max 10MB each)</p>
          </div>
        </div>

        {/* File List */}
        {filesToUpload.length > 0 && (
          <div className='space-y-2'>
            <p className='text-sm font-medium text-muted-foreground'>Selected Files ({filesToUpload.length})</p>
            {filesToUpload.map((file, index) => {
              const fileType = getFileTypeFromMime(file.type);
              return (
                <div
                  key={index}
                  className='flex items-center gap-3 p-3 rounded-lg border bg-white hover:bg-gray-50 transition-colors'
                >
                  {getFileIcon(fileType)}
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-muted-foreground truncate'>{file.name}</p>
                    <p className='text-xs text-muted-foreground'>{formatFileSize(file.size)}</p>
                  </div>
                  <Button
                    type='button'
                    variant='ghost'
                    size='sm'
                    onClick={(e) => {
                      e.stopPropagation();
                      removeFile(index);
                    }}
                    className='text-red-500 hover:text-red-700 hover:bg-red-50'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              );
            })}

            {/* Upload Button (only in upload mode) */}
            {isUploadMode && (
              <Button
                onClick={handleUploadFiles}
                disabled={uploadMutation.isPending || filesToUpload.length === 0}
                className='w-full bg-cases-primary hover:bg-cases-primary/90 text-white'
              >
                {uploadMutation.isPending ? (
                  <>
                    <Spinner size='sm' className='mr-2' />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className='h-4 w-4 mr-2' />
                    Upload {filesToUpload.length} File{filesToUpload.length > 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className='space-y-6'>
      {showHeader && (
        <div>
          <h3 className='text-sm font-semibold text-foreground mb-2'>{title}</h3>
          <p className='text-xs text-muted-foreground mb-4'>{description}</p>
        </div>
      )}

      {/* Drag and Drop Zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'relative border-2 border-dashed rounded-xl p-8 transition-all duration-200',
          isDragging
            ? 'border-cases-primary bg-emerald-50/50 scale-[1.02]'
            : 'border-slate-300 hover:border-cases-primary/50 hover:bg-slate-50'
        )}
      >
        <input
          ref={fileInputRef}
          type='file'
          multiple
          accept={SUPPORTED_FILE_TYPES.join(',')}
          onChange={(e) => handleFileSelect(e.target.files)}
          className='hidden'
        />

        <div className='flex flex-col items-center justify-center text-center'>
          <div className='mb-4 p-4 rounded-full bg-cases-primary/10'>
            <Upload className='h-8 w-8 text-cases-primary' />
          </div>
          <h4 className='text-lg font-semibold mb-2'>{isDragging ? 'Drop files here' : 'Drag and drop files here'}</h4>
          <p className='text-sm text-muted-foreground mb-4'>or</p>
          <Button
            type='button'
            variant='outline'
            onClick={() => fileInputRef.current?.click()}
            className='bg-cases-primary text-white hover:bg-cases-primary/90 border-none'
          >
            <Paperclip className='h-4 w-4 mr-2' />
            Choose Files
          </Button>
        </div>
      </div>

      {/* Files Preview List */}
      {filesToUpload.length > 0 && (
        <div className='space-y-3'>
          <div className='flex items-center justify-between'>
            <h4 className='text-sm font-semibold'>
              {isUploadMode ? 'Files to Upload' : 'Selected Files'} ({filesToUpload.length})
            </h4>
            <Button variant='ghost' size='sm' onClick={clearAllFiles} className='text-xs h-8'>
              Clear All
            </Button>
          </div>

          <div className='space-y-2 max-h-64 overflow-y-auto'>
            {filesToUpload.map((file, index) => {
              const fileType = getFileTypeFromMime(file.type);
              const fileUrl = file.type.startsWith('image/') ? URL.createObjectURL(file) : null;

              return (
                <div
                  key={index}
                  className='flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-200 hover:border-cases-primary/50 transition-colors'
                >
                  {/* File Preview/Icon */}
                  <div className='flex-shrink-0'>
                    {fileUrl ? (
                      <div className='w-12 h-12 rounded overflow-hidden'>
                        <img src={fileUrl} alt={file.name} className='w-full h-full object-cover' />
                      </div>
                    ) : (
                      <div className='w-12 h-12 rounded bg-slate-200 flex items-center justify-center'>
                        {getFileIcon(fileType)}
                      </div>
                    )}
                  </div>

                  {/* File Info */}
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium truncate'>{file.name}</p>
                    <div className='flex items-center gap-2 mt-1'>
                      <Badge variant='outline' className='text-[10px] h-5'>
                        {fileType.toUpperCase()}
                      </Badge>
                      <span className='text-xs text-muted-foreground'>{formatFileSize(file.size)}</span>
                    </div>
                  </div>

                  {/* Remove Button */}
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={() => removeFile(index)}
                    className='flex-shrink-0 h-8 w-8 text-red-500 hover:text-red-700 hover:bg-red-50'
                  >
                    <Trash2 className='h-4 w-4' />
                  </Button>
                </div>
              );
            })}
          </div>

          {/* Upload Button (only in upload mode) */}
          {isUploadMode && (
            <Button
              onClick={handleUploadFiles}
              disabled={uploadMutation.isPending || filesToUpload.length === 0}
              className='w-full bg-cases-primary hover:bg-cases-primary/90 text-white'
            >
              {uploadMutation.isPending ? (
                <>
                  <Spinner size='sm' className='mr-2' />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className='h-4 w-4 mr-2' />
                  Upload {filesToUpload.length} File{filesToUpload.length > 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};
